import { FC, ReactNode, ReactNodeArray, useCallback, useEffect, useMemo, useState } from 'react';
import './ColumnSlider.styles.scss';
import { getSlides, getSlidesData } from './ColumnSlider.helpers';
import { IconArrowLeft, IconArrowRight } from '@icons';
import cn from 'classnames';
import { isDivisionRemainder } from '@core/helpers';
import VisibilitySensor from 'react-visibility-sensor';
import SliderNav from "@components/new/SliderNav/SliderNav";

const DEFAULT_AUTO_SLIDE_INTERVAL = 5000;

interface iProps {
    children: ReactNode[];
    slidesPerView?: number;
    isNewNav?: boolean;
    setActiveTileIndex?: (index: number | null) => void;
    autoSlideIntervalMs?: number;
    isAutoSlideDisabled?: boolean;
    hideNav?: boolean;
    onNavReady?: (handlers: { next: () => void; prev: () => void }) => void;
}

const ColumnSlider: FC<iProps> = (
    {
        children,
        slidesPerView = 5,
        isNewNav,
        setActiveTileIndex,
        autoSlideIntervalMs = DEFAULT_AUTO_SLIDE_INTERVAL,
        isAutoSlideDisabled = false,
        hideNav = false,
        onNavReady,
    },
) => {
    const flatSlides = children.flat().filter(item => item);
    const [sliderMove, setSliderMove] = useState(0);
    const [isVisible, setIsVisible] = useState(false);
    const [lastMove, setLastMove] = useState(-1);
    const [isAutoSlideEnabled, setIsAutoSlideEnabled] = useState(!isAutoSlideDisabled);

    useEffect(() => {
        setIsAutoSlideEnabled(!isAutoSlideDisabled);
    }, [isAutoSlideDisabled]);

    const slidesData = getSlidesData(sliderMove, lastMove, slidesPerView, flatSlides.length);
    const hasEnoughItems = flatSlides.length > slidesPerView;

    const currentSlideFirstTile = useMemo(
        () => {
            const currentSlide = slidesData.find(s => s.isCurrent);

            if (currentSlide) {
                const firstTile = getSlides(flatSlides, currentSlide.count, currentSlide.startIndex)[0] ?? null;

                if (firstTile) {
                    return flatSlides.indexOf(firstTile) ?? null;
                }
            }

            return null;
        },
        [slidesData, flatSlides]
    );

    useEffect(() => {
        setActiveTileIndex?.(currentSlideFirstTile);
    }, [currentSlideFirstTile, setActiveTileIndex]);

    const moveSlider = useCallback((move: number) => {
        setLastMove(move);
        setSliderMove((prevIndex) => prevIndex + move);
    }, []);

    const handleMoveForward = useCallback(() => {
        moveSlider(1);
    }, [moveSlider]);

    const handleMoveBackward = useCallback(() => {
        moveSlider(-1);
    }, [moveSlider]);

    useEffect(() => {
        onNavReady?.({ next: handleMoveForward, prev: handleMoveBackward });
    }, [onNavReady, handleMoveForward, handleMoveBackward]);

    const handleWindowKeyDown = useCallback((event: KeyboardEvent) => {
        if (event.key === 'ArrowRight') {
            handleMoveForward();
        }
        if (event.key === 'ArrowLeft') {
            handleMoveBackward();
        }
    }, [handleMoveForward, handleMoveBackward]);

    useEffect(() => {
        if (isVisible) {
            window.addEventListener('keydown', handleWindowKeyDown);
        }
        return () => {
            window.removeEventListener('keydown', handleWindowKeyDown);
        };
    }, [isVisible, handleWindowKeyDown]);

    const renderSlides = (slides: ReactNodeArray) => {
        return slides.map((slide, index) => {
            const isFirstVisible = isDivisionRemainder(slidesPerView, index + 1, 1);
            const classNames = cn(
                'ColumnSlider__slide',
                isFirstVisible && 'is-first-visible',
                isDivisionRemainder(slidesPerView, index + 1, 0) && 'is-last-visible',
            );
            return (
                <div
                    key={flatSlides.indexOf(slide)}
                    className={classNames}
                    style={{
                        width: `calc((100% - ${slidesPerView * 8}px) / ${slidesPerView})`,
                        maxWidth: `calc((100% - ${slidesPerView * 8}px) / ${slidesPerView})`
                    }}
                >
                    {slide}
                </div>
            );
        });
    };

    return (
        <div className={cn(
            "ColumnSlider",
            isNewNav && 'is-new-nav',
        )}>
            <div className="ColumnSlider__viewport">
                <VisibilitySensor
                    onChange={(isVisible) => {
                        setIsVisible(isVisible);
                    }}
                    partialVisibility={false}
                    offset={{
                        top: 100,
                        bottom: 100,
                    }}
                >
                    <div className="ColumnSlider__sensor"/>
                </VisibilitySensor>
                <div
                    className="ColumnSlider__slide ColumnSlider__slide--hidden"
                    style={{
                        width: `calc((100% - ${slidesPerView * 8}px) / ${slidesPerView})`,
                    }}
                >
                    {flatSlides[0]}
                </div>
                {hasEnoughItems ? (
                    <>
                        {!hideNav && isNewNav && (
                            <SliderNav
                                key={sliderMove}
                                onNext={handleMoveForward}
                                onPrev={handleMoveBackward}
                                autoSlideIntervalMs={isAutoSlideEnabled ? autoSlideIntervalMs : undefined}
                            />
                        )}
                        {!hideNav && !isNewNav && (<>
                            <div
                                onClick={handleMoveBackward}
                                className="ColumnSlider__nav ColumnSlider__nav--prev"
                            >
                                <IconArrowLeft className="Icon32"/>
                            </div>
                            <div
                                onClick={handleMoveForward}
                                className="ColumnSlider__nav ColumnSlider__nav--next"
                            >
                                <IconArrowRight className="Icon32"/>
                            </div>
                        </>)}
                        {Array(5).fill(null).map((_, i) => (
                            <div
                                key={i}
                                className={cn(
                                    "ColumnSlider__view",
                                    slidesData[i].isCurrent && 'is-current',
                                    slidesData[i].isPrev && 'is-prev',
                                    slidesData[i].isNext && 'is-next',
                                )}
                                onMouseEnter={() => isAutoSlideDisabled ? null : setIsAutoSlideEnabled(false)}
                                onMouseLeave={() => isAutoSlideDisabled ? null : setIsAutoSlideEnabled(true)}
                                style={{
                                    transitionDuration: slidesData[i].duration,
                                    transform: `translateX(${slidesData[i].translateX})`,
                                    justifyContent: `flex-${slidesData[i].position}`,
                                }}
                            >
                                {renderSlides(getSlides(flatSlides, slidesData[i].count, slidesData[i].startIndex))}
                            </div>
                        ))}
                    </>
                ) : (
                    <div className="ColumnSlider__view is-only">
                        {renderSlides(flatSlides)}
                    </div>
                )}
            </div>
        </div>
    );
};

export default ColumnSlider;